import React, { useEffect, useRef, useState } from "react";
import { Radar } from "./Finding";
import type { Seat, SeatRatings } from "./Found";
import RankInsignia from "../../shared/rating/RankInsignia";
import { wormColors } from "core/src/core/constants.js";
import { fmtClock } from "../lobby/lobby";
import s from "./ranked.module.scss";

// The 6 FFA search (sketch 7 in /debug/ffa-finding): no card of its own, the
// six seats are the cards, two columns by three rows. A seat someone is in
// shows their flag, name and rank; an empty one scans, its own radar and
// "Finding enemy". When a player lands, the seat flips twice: the scan flips
// out, "Target acquired!" flips in, holds, and flips out to the player. The
// clock and Cancel are bare text: under the rows on a desktop (the rows sit
// under the map), above them on a phone (the rows sit above the arena). The
// clock keeps counting through the hand-off; Cancel goes dead once all six
// are in.

// A landed seat's flip: "Target acquired!" for ACQ_MS, then the player.
const ACQ_MS = 1300;
const IN_MS = 900;
type Phase = "acq" | "in";

export default function FindingSeats({ seats, total, youIds, ratings, since, found = false, onCancel, onPlayNow, phone = false }: {
  seats: Seat[];          // who is in so far, you first
  total: number;          // seats in the match (6)
  youIds: string[];
  ratings: SeatRatings;   // known once the match starts; eggs before
  since: number;          // ms epoch the search started
  found?: boolean;
  onCancel?: () => void;
  onPlayNow?: () => void; // play Play Now while the search runs
  phone?: boolean;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const h = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(h);
  }, []);
  const secs = Math.max(0, Math.floor((now - since) / 1000));

  // Seats that landed after mount run the flip; the ones already in at mount
  // (you) just sit there.
  const [phases, setPhases] = useState<Record<number, Phase>>({});
  const seen = useRef(seats.length);
  useEffect(() => {
    if (seats.length <= seen.current) { seen.current = seats.length; return; }
    const timers: number[] = [];
    for (let i = seen.current; i < seats.length; i++) {
      setPhases((p) => ({ ...p, [i]: "acq" }));
      timers.push(window.setTimeout(() => setPhases((p) => ({ ...p, [i]: "in" })), ACQ_MS));
      timers.push(window.setTimeout(() => setPhases((p) => { const n = { ...p }; delete n[i]; return n; }), ACQ_MS + IN_MS));
    }
    seen.current = seats.length;
    return () => timers.forEach(clearTimeout);
  }, [seats.length]);

  const meta = (
    <div className={s.seatsMeta}>
      <span className={s.seatsClock}>{fmtClock(secs)}</span>
      {onPlayNow && <button type="button" className={s.seatsCancel} onClick={onPlayNow} disabled={found || seats.length >= total}>Play while you wait</button>}
      {onCancel && <button type="button" className={s.seatsCancel} onClick={onCancel} disabled={found || seats.length >= total}>Cancel</button>}
    </div>
  );
  // Each layer keyed: across a phase change React must swap the nodes, not
  // repaint them, or the flip animations never restart.
  const scanning = (
    <div key="scan" className={s.seatLayer}>
      <Radar size={phone ? 24 : 30} />
      <span className={s.seatFinding}>Finding enemy<i className={s.fdots}><b /><b /><b /></i></span>
    </div>
  );
  const acquired = (
    <div key="acq" className={`${s.seatLayer} ${s.seatAcquired}`}>
      <Radar size={phone ? 24 : 30} found />
      <span className={s.seatFinding}>Target acquired!</span>
    </div>
  );
  const tiles = (
    <div className={s.seats} aria-live="polite" aria-label={`${seats.length} of ${total} players found`}>
      {Array.from({ length: total }, (_, i) => {
        const seat = seats[i];
        if (!seat) return <div key={"empty" + i} className={s.seat}>{scanning}</div>;
        const r = ratings ? ratings[seat.id] : null;
        const you = youIds.includes(seat.id);
        const player = (
          <div key="player" className={s.seatLayer}>
            {seat.flag ? <img className={s.seatFlag} src={`https://flagcdn.com/w40/${seat.flag}.png`} alt="" /> : <span className={s.seatFlagNone} />}
            <span className={s.seatName}>{seat.name}</span>
            {r && r.rating != null ? <RankInsignia value={r.rating} size={phone ? 18 : 22} /> : <RankInsignia rating={null} placeholder size={phone ? 18 : 22} />}
          </div>
        );
        const phase = phases[i];
        return (
          <div key={seat.id} className={`${s.seat} ${s.seatOn} ${you ? s.seatYou : ""} ${phase === "acq" ? s.seatFlipAcq : phase === "in" ? s.seatFlipIn : ""}`} style={{ "--worm": wormColors[seat.colorId] || "#28c4d4" } as React.CSSProperties}>
            {phase === "acq" ? [scanning, acquired] : phase === "in" ? [acquired, player] : [player]}
          </div>
        );
      })}
    </div>
  );
  return phone ? <div className={`${s.seatsWrap} ${s.seatsWrapPhone}`}>{meta}{tiles}</div> : <div className={s.seatsWrap}>{tiles}{meta}</div>;
}
